<template>
  <div class="m-2">
    <div>
      <div class="mainheader d-flex justify-content-left p-2">
        <span>TAIR BLAST 2.9.0+</span>
        <img
          src="@/assets/diamond.svg"
          height="20px"
          alt="Premium"
          class="ml-2"
        />
      </div>

      <div class="d-flex justify-content-left">
        <p class="pl-2 text-left">
          <b>
            This form uses
            <a
              href="https://blast.ncbi.nlm.nih.gov/Blast.cgi?CMD=Web&PAGE_TYPE=BlastDocs"
              target="_blank"
              >NCBI BLAST 2.9.0+</a
            >

            <br />
            <span
              >View help documentation
              <a
                href="https://phoenixbioinformatics.atlassian.net/wiki/spaces/COM/pages/42217028/TAIR+BLAST+Help"
                target="_blank"
                >here</a
              ></span
            >
          </b>
        </p>
      </div>
    </div>
    <div class="container p-2 border rounded shadow-sm form-bg">
      <fieldset id="results" class="border border-dark rounded p-2">
        <span class="pl-4 mb-3 d-flex justify-content-left">
          <b>Blast</b>
        </span>
        <div class="form-group row">
          <label for="algorithm" class="col-sm-4 col-form-label text-left"
            >BLAST™ program:</label
          >
          <div class="col-sm-4">
            <select
              id="algorithm"
              class="form-control"
              v-model="blastForm.algorithm"
              @change="onAlgorithmChange"
              required
            >
              <option
                v-for="option in algorithmOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.text }}
              </option>
            </select>
          </div>
        </div>
        <div class="form-group row">
          <label for="default_db" class="col-sm-4 col-form-label text-left"
            >Datasets:</label
          >
          <div class="col-sm-4">
            <select
              id="default_db"
              class="form-control"
              v-model="blastForm.dataset"
              :disabled="!datasetGroups.length"
              required
            >
              <optgroup
                v-for="group in datasetGroups"
                :key="group.name"
                :label="group.name"
              >
                <option
                  v-for="option in group.options"
                  :key="option.id"
                  :value="option.id"
                >
                  {{ option.label }}
                </option>
              </optgroup>
            </select>
          </div>
        </div>
        <div class="form-group row">
          <label class="col-sm-4 col-form-label text-left">Input Type:</label>
          <div class="col-sm-4 d-flex align-items-center">
            <div class="form-check form-check-inline">
              <input
                class="form-check-input"
                type="radio"
                name="inputType"
                id="querySequence"
                value="sequence"
                v-model="blastForm.inputType"
                required
              />
              <label class="form-check-label" for="querySequence"
                >Query Sequence</label
              >
            </div>
            <div class="form-check form-check-inline">
              <input
                class="form-check-input"
                type="radio"
                name="inputType"
                id="locusName"
                value="locus"
                v-model="blastForm.inputType"
                required
              />
              <label class="form-check-label text-left pl-2" for="locusName"
                >Locus Name (At1g01030)</label
              >
            </div>
          </div>
        </div>
        <div class="form-group row">
          <label for="inputText" class="col-sm-4 col-form-label text-left"
            >Input:</label
          >
          <div class="col-sm-4">
            <textarea
              id="inputText"
              class="form-control"
              v-model="blastForm.query"
              rows="6"
              :placeholder="
                blastForm.inputType === 'locus'
                  ? 'One or more locus or gene model names, e.g. AT1G01030'
                  : 'Enter your query sequence (raw or FASTA)'
              "
              required
            ></textarea>
          </div>
        </div>
        <div class="form-group row">
          <label for="fileInput" class="col-sm-4 col-form-label text-left"
            >Choose a file:</label
          >
          <div class="col-sm-4 d-flex align-items-center">
            <input
              type="file"
              id="fileInput"
              @change="handleFileUpload"
              aria-label="fileInput"
              style="display: none"
            />
            <b-button size="sm" @click="triggerFileInputClick" class="mt-2"
              >Choose File</b-button
            >
            <span class="ml-2">{{ chosenFilename }}</span>
          </div>
        </div>
        <div class="form-group row">
          <p class="col-sm-8 text-left mb-1">
            Raw and FASTA formats accepted; several FASTA sequences can be
            searched at once.
          </p>
        </div>
        <div class="form-group row">
          <div class="col-sm-4 form-check form-check-inline pl-4">
            <input
              class="form-check-input"
              type="checkbox"
              id="filterQuery"
              v-model="blastForm.filter"
            />
            <label class="form-check-label text-left pl-2" for="filterQuery"
              >Filter Query</label
            >
          </div>
        </div>
      </fieldset>
      <fieldset id="advanced" class="border border-dark rounded p-2 mt-4">
        <div class="d-flex justify-content-start align-items-center pl-4 mb-3">
          <button
            type="button"
            @click="toggleAdvancedOptions"
            :aria-expanded="advancedOptionsVisible ? 'true' : 'false'"
            aria-controls="advancedOptions"
            :aria-label="
              advancedOptionsVisible
                ? 'Hide advanced options'
                : 'Show advanced options'
            "
          >
            {{ advancedOptionsVisible ? "-" : "+" }}
          </button>
          <b class="ml-4">Advanced BLAST™ Parameter Options</b>
        </div>
        <div v-if="advancedOptionsVisible" id="advancedOptions">
          <div class="form-group row">
            <label for="matrix" class="col-sm-4 col-form-label text-left"
              >Weight Matrix:</label
            >
            <div class="col-sm-4">
              <select
                name="Matrix"
                id="matrix"
                class="form-control"
                v-model="blastForm.matrix"
              >
                <option
                  v-for="option in matrixOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.text }}
                </option>
              </select>
            </div>
          </div>
          <div class="form-group row">
            <label for="maxScores" class="col-sm-4 col-form-label text-left"
              >Max Scores:</label
            >
            <div class="col-sm-4">
              <input
                type="text"
                name="MaxScores"
                id="maxScores"
                class="form-control"
                v-model="blastForm.maxScores"
              />
            </div>
          </div>
          <div class="form-group row">
            <label for="maxAlignments" class="col-sm-4 col-form-label text-left"
              >Max Alignments:</label
            >
            <div class="col-sm-4">
              <input
                type="text"
                name="MaxAlignments"
                id="maxAlignments"
                class="form-control"
                v-model="blastForm.maxAlignments"
              />
            </div>
          </div>
          <div class="form-group row">
            <label
              for="gappedAlignments"
              class="col-sm-4 col-form-label text-left"
              >Gapped Alignments:</label
            >
            <div class="col-sm-4">
              <select
                name="GappedAlignment"
                id="gappedAlignments"
                class="form-control"
                v-model="blastForm.gapped"
              >
                <option
                  v-for="option in gappedAlignmentOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.text }}
                </option>
              </select>
            </div>
          </div>
          <div class="form-group row">
            <label for="expectation" class="col-sm-4 col-form-label text-left"
              >Expectation:</label
            >
            <div class="col-sm-4">
              <select
                name="Expectation"
                id="expectation"
                class="form-control"
                v-model="blastForm.expectation"
              >
                <option
                  v-for="option in expectationOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.text }}
                </option>
              </select>
            </div>
          </div>
          <div class="form-group row">
            <label
              for="nucleicMismatch"
              class="col-sm-4 col-form-label text-left"
              >Nucleic Mismatch:</label
            >
            <div class="col-sm-4">
              <input
                type="text"
                name="NucleicMismatch"
                id="nucleicMismatch"
                class="form-control"
                v-model="blastForm.nucleicMismatch"
              />
            </div>
          </div>
          <div class="form-group row">
            <label for="nucleicMatch" class="col-sm-4 col-form-label text-left"
              >Nucleic Match:</label
            >
            <div class="col-sm-4">
              <input
                type="text"
                name="NucleicMatch"
                id="nucleicMatch"
                class="form-control"
                v-model="blastForm.nucleicMatch"
              />
            </div>
          </div>
          <div class="form-group row">
            <label for="openPenalty" class="col-sm-4 col-form-label text-left"
              >Gap Opening Penalty:</label
            >
            <div class="col-sm-4">
              <input
                type="text"
                name="OpenPenalty"
                id="openPenalty"
                class="form-control"
                v-model="blastForm.gapOpen"
              />
            </div>
          </div>
          <div class="form-group row">
            <label for="extendPenalty" class="col-sm-4 col-form-label text-left"
              >Gap Extension Penalty:</label
            >
            <div class="col-sm-4">
              <input
                type="text"
                name="ExtendPenalty"
                id="extendPenalty"
                class="form-control"
                v-model="blastForm.gapExtend"
              />
            </div>
          </div>
          <div class="form-group row">
            <label
              for="extensionThreshold"
              class="col-sm-4 col-form-label text-left"
              >Extension Threshold:</label
            >
            <div class="col-sm-4">
              <input
                type="text"
                name="ExtensionThreshold"
                id="extensionThreshold"
                class="form-control"
                v-model="blastForm.extensionThreshold"
              />
            </div>
          </div>
          <div class="form-group row">
            <label for="wordSize" class="col-sm-4 col-form-label text-left"
              >Word Size:</label
            >
            <div class="col-sm-4">
              <input
                type="text"
                name="WordSize"
                id="wordSize"
                class="form-control"
                v-model="blastForm.wordSize"
              />
            </div>
          </div>
          <div class="form-group row">
            <label
              for="queryGeneticCode"
              class="col-sm-4 col-form-label text-left"
              >Query and Database Genetic Code:</label
            >
            <div class="col-sm-4">
              <select
                name="QueryGeneticCode"
                id="queryGeneticCode"
                class="form-control"
                v-model="blastForm.geneticCode"
              >
                <option
                  v-for="option in geneticCodeOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.text }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </fieldset>
      <fieldset id="results" class="border border-dark rounded p-2 mt-4">
        <span class="pl-4 mb-3 d-flex justify-content-left">
          <b>Results Return Options</b>
        </span>
        <div class="form-group row">
          <label for="comment" class="col-sm-4 col-form-label text-left"
            >Output Title:</label
          >
          <div class="col-sm-4">
            <input
              type="text"
              name="Comment"
              id="comment"
              class="form-control"
              v-model="blastForm.title"
              maxlength="200"
              placeholder="Optional, will be added to the output for your use"
            />
          </div>
        </div>
        <div class="text-left pb-2">
          <span
            >Please be patient: a search may take a minute or two. Results open
            on their own page, can be downloaded as text, and are kept for an
            hour.</span
          >
        </div>
      </fieldset>
      <div
        v-if="errorMessage"
        class="alert alert-danger text-left mt-3"
        role="alert"
        style="white-space: pre-line"
      >
        {{ errorMessage }}
      </div>
      <div class="form-group row">
        <div class="col-sm-4 offset-sm-1 d-flex justify-content-left mt-2">
          <button
            type="button"
            class="btn btn-primary"
            @click="handleSubmit"
            :disabled="submitting || !blastForm.dataset"
            :aria-busy="submitting ? 'true' : 'false'"
          >
            <span
              v-if="submitting"
              class="spinner-border spinner-border-sm mr-2"
              aria-hidden="true"
            ></span>
            Run BLAST
          </button>
          <button
            type="button"
            class="ml-4 btn btn-secondary"
            @click="handleReset"
          >
            Reset
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PostsService from "@/services/PostsService";

// Protein (AA) datasets for blastp / blastx, nucleotide for the others.
const PROTEIN_DB_ALGORITHMS = ["blastp", "blastx"];
const PROTEIN_QUERY_ALGORITHMS = ["blastp", "tblastn"];

const defaultForm = () => ({
  algorithm: "blastn",
  dataset: "",
  inputType: "sequence",
  query: "",
  filter: true,
  matrix: "",
  maxScores: 100,
  maxAlignments: 50,
  nucleicMismatch: -3,
  nucleicMatch: 2,
  gapOpen: "0 (use default)",
  gapExtend: "0 (use default)",
  extensionThreshold: "0 (use default)",
  wordSize: "0 (use default)",
  expectation: "10",
  geneticCode: "1",
  gapped: "T",
  title: "",
});

export default {
  data: () => ({
    blastForm: defaultForm(),
    datasets: [],
    submitting: false,
    errorMessage: "",
    advancedOptionsVisible: false,
    algorithmOptions: [
      { text: "BLASTN: NT query, NT db", value: "blastn" },
      { text: "BLASTP: AA query, AA db", value: "blastp" },
      { text: "BLASTX: NT query, AA db", value: "blastx" },
      { text: "TBLASTN: AA query, NT db", value: "tblastn" },
      { text: "TBLASTX: NT query, NT db (All 6 Frames)", value: "tblastx" },
    ],
    matrixOptions: [
      { value: "", text: "Program default" },
      { value: "BLOSUM45", text: "Blosum45" },
      { value: "BLOSUM50", text: "Blosum50" },
      { value: "BLOSUM62", text: "Blosum62" },
      { value: "BLOSUM80", text: "Blosum80" },
      { value: "BLOSUM90", text: "Blosum90" },
      { value: "PAM30", text: "Pam30" },
      { value: "PAM70", text: "Pam70" },
      { value: "PAM250", text: "Pam250" },
    ],
    expectationOptions: [
      { value: "0.0001", text: "0.0001" },
      { value: "0.001", text: "0.001" },
      { value: "0.01", text: "0.01" },
      { value: "0.1", text: "0.1" },
      { value: "1", text: "1" },
      { value: "10", text: "10" },
      { value: "100", text: "100" },
      { value: "1000", text: "1000" },
    ],
    geneticCodeOptions: [
      { value: "1", text: "Standard or Universal (default)" },
      { value: "2", text: "Vertebrate Mitochondrial" },
      { value: "3", text: "Yeast Mitochondrial" },
      { value: "4", text: "Mold Mitochondrial" },
      { value: "5", text: "Invertebrate Mitochondrial" },
      { value: "6", text: "Ciliate Nuclear" },
      { value: "9", text: "Echinoderm Mitochondrial" },
      { value: "10", text: "Euplotid Nuclear" },
      { value: "11", text: "Bacterial" },
      { value: "12", text: "Alternative Yeast Nuclear" },
      { value: "13", text: "Ascidian Mitochondrial" },
      { value: "14", text: "Flatworm Mitochondrial" },
      { value: "15", text: "Blepharisma Macronuclear" },
    ],
    gappedAlignmentOptions: [
      { value: "T", text: "Yes" },
      { value: "F", text: "No" },
    ],
    chosenFilename: "No file chosen",
  }),
  computed: {
    datasetType() {
      return PROTEIN_DB_ALGORITHMS.includes(this.blastForm.algorithm)
        ? "AA"
        : "DNA";
    },
    // Datasets for the chosen algorithm, as <optgroup>s in server order.
    datasetGroups() {
      const groups = [];
      for (const d of this.datasets) {
        if (d.type !== this.datasetType) continue;
        let group = groups[groups.length - 1];
        if (!group || group.name !== d.group) {
          group = { name: d.group, options: [] };
          groups.push(group);
        }
        group.options.push(d);
      }
      return groups;
    },
  },
  async created() {
    // Links from sequence / protein pages prefill the query.
    const { sequence_type: sequenceType, sequence } = this.$route.query;
    if (sequence) this.blastForm.query = sequence;
    if (sequenceType === "protein") this.blastForm.algorithm = "blastp";
    this.onAlgorithmChange();
    await this.loadDatasets();
  },
  methods: {
    async loadDatasets() {
      try {
        const { data } = await PostsService.getBlastDatasets();
        this.datasets = data.datasets || [];
        this.selectDefaultDataset();
      } catch (err) {
        this.errorMessage =
          "Could not load the BLAST datasets. Please reload the page.";
      }
    },
    selectDefaultDataset() {
      const ok = this.datasets.some(
        (d) => d.id === this.blastForm.dataset && d.type === this.datasetType
      );
      if (!ok) {
        const first = this.datasets.find((d) => d.type === this.datasetType);
        this.blastForm.dataset = first ? first.id : "";
      }
    },
    onAlgorithmChange() {
      this.blastForm.matrix = PROTEIN_QUERY_ALGORITHMS.includes(
        this.blastForm.algorithm
      )
        ? "BLOSUM62"
        : "";
      this.selectDefaultDataset();
    },
    async handleSubmit() {
      this.errorMessage = "";
      this.submitting = true;
      try {
        const { data } = await PostsService.submitBlast({
          ...this.blastForm,
          gapped: this.blastForm.gapped === "T",
        });
        this.$router.push({
          name: "BlastResults",
          params: { jobId: data.jobId },
        });
      } catch (err) {
        this.errorMessage =
          (err.response && err.response.data && err.response.data.error) ||
          "BLAST could not be started. Please try again later.";
      } finally {
        this.submitting = false;
      }
    },
    handleReset() {
      this.blastForm = defaultForm();
      this.errorMessage = "";
      this.chosenFilename = "No file chosen";
      this.onAlgorithmChange();
    },
    toggleAdvancedOptions() {
      this.advancedOptionsVisible = !this.advancedOptionsVisible;
    },
    triggerFileInputClick(event) {
      event.stopPropagation();
      this.$nextTick(() => {
        document.getElementById("fileInput").click();
      });
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.chosenFilename = file.name;
        const reader = new FileReader();
        reader.onload = (e) => {
          this.blastForm.query = e.target.result;
        };
        reader.readAsText(file);
      }
    },
  },
};
</script>

<style scoped>
.mainheader {
  font-size: 12pt;
  font-weight: bold;
  color: #930;
}
.container {
  max-width: 100%;
}
.form-bg {
  background: #f1efec;
}
p {
  font-size: 12px;
}
.form-group {
  margin-bottom: 0.5rem;
}
</style>
